import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Shield, 
  Mail, 
  Loader2, 
  AlertTriangle,
  CheckCircle,
  Copy,
  KeyRound,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * MFA Settings Component - Allow users to enable/disable MFA
 */
const MFASettings = ({ isOpen, onClose, getAuthHeaders }) => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [password, setPassword] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchMfaStatus();
    }
  }, [isOpen]);

  const fetchMfaStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/mfa/status`, getAuthHeaders());
      setMfaStatus(response.data);
    } catch (err) {
      toast.error('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMfa = async () => {
    setEnabling(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/mfa/enable`,
        { method: 'email' },
        getAuthHeaders()
      );
      
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      setMfaStatus({ ...mfaStatus, mfa_enabled: true, mfa_method: 'email' });
      toast.success('MFA has been enabled!');
    } catch (err) {
      toast.error('Failed to enable MFA');
    } finally {
      setEnabling(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setDisabling(true);
    try {
      const formData = new FormData();
      formData.append('password', password);

      await axios.post(`${API_URL}/api/auth/mfa/disable`, formData, getAuthHeaders());
      
      setMfaStatus({ ...mfaStatus, mfa_enabled: false });
      setShowDisableConfirm(false);
      setPassword('');
      toast.success('MFA has been disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setRegenerating(true);
    try {
      const formData = new FormData();
      formData.append('password', password);

      const response = await axios.post(
        `${API_URL}/api/auth/mfa/regenerate-backup-codes`,
        formData,
        getAuthHeaders()
      );
      
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      setShowRegenerateConfirm(false);
      setPassword('');
      toast.success('Backup codes have been regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate backup codes');
    } finally {
      setRegenerating(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success('Backup codes copied to clipboard');
  };

  const handleClose = () => {
    setShowDisableConfirm(false);
    setShowRegenerateConfirm(false);
    setShowBackupCodes(false);
    setPassword('');
    setBackupCodes([]);
    onClose();
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md bg-slate-50 dark:bg-slate-800">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show Backup Codes
  if (showBackupCodes && backupCodes.length > 0) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <KeyRound className="w-5 h-5 text-amber-500" />
              Save Your Backup Codes
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Store these codes securely. Each can only be used once.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Important:</strong> These codes will not be shown again. 
                  Save them in a secure location like a password manager.
                </span>
              </p>
            </div>

            <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div 
                    key={index}
                    className="font-mono text-sm bg-white dark:bg-slate-800 px-3 py-2 rounded border border-slate-200 dark:border-slate-600 text-center text-slate-900 dark:text-slate-100"
                  >
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <Button
              variant="outline"
              onClick={copyBackupCodes}
              className="w-full bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy All Codes
            </Button>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowBackupCodes(false)}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              I&apos;ve Saved My Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Disable MFA Confirmation
  if (showDisableConfirm) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              This will reduce the security of your account. Enter your password to confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-xs text-red-600 dark:text-red-400">
                Warning: Disabling MFA makes your account more vulnerable to unauthorized access.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Password</Label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowDisableConfirm(false); setPassword(''); }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisableMfa}
              disabled={disabling || !password}
              className="flex-1"
            >
              {disabling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Disable MFA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Regenerate Backup Codes Confirmation
  if (showRegenerateConfirm) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Regenerate Backup Codes
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              This will invalidate all existing backup codes. Enter your password to confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Password</Label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowRegenerateConfirm(false); setPassword(''); }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateBackupCodes}
              disabled={regenerating || !password}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Main MFA Settings View
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <Shield className="w-5 h-5 text-teal-500" />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Add an extra layer of security to your account
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* MFA Status */}
          <div className={`p-4 rounded-lg border ${
            mfaStatus?.mfa_enabled 
              ? 'bg-teal-500/10 border-teal-500/30' 
              : 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  mfaStatus?.mfa_enabled ? 'bg-teal-500/20' : 'bg-slate-200 dark:bg-slate-600'
                }`}>
                  <Mail className={`w-5 h-5 ${
                    mfaStatus?.mfa_enabled ? 'text-teal-600' : 'text-slate-500'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    Email Verification
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Receive a code via email when signing in
                  </p>
                </div>
              </div>
              <Badge className={mfaStatus?.mfa_enabled 
                ? 'bg-teal-500/20 text-teal-600 border-teal-500/30' 
                : 'bg-slate-200 dark:bg-slate-600 text-slate-500'
              }>
                {mfaStatus?.mfa_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </div>

          {/* Compliance Notice */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              MFA is recommended for 21 CFR Part 11 compliance
            </p>
          </div>

          {/* Actions */}
          {mfaStatus?.mfa_enabled ? (
            <div className="space-y-2">
              <Button
                variant="outline"
                onClick={() => setShowRegenerateConfirm(true)}
                className="w-full justify-start bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Regenerate Backup Codes
                {mfaStatus?.backup_codes_count > 0 && (
                  <Badge className="ml-auto bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                    {mfaStatus.backup_codes_count} remaining
                  </Badge>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDisableConfirm(true)}
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 border-slate-300 dark:border-slate-600"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Disable Two-Factor Authentication
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleEnableMfa}
              disabled={enabling}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white"
            >
              {enabling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 mr-2" />
              )}
              Enable Two-Factor Authentication
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="w-full">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MFASettings;
